import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

import { handleFetch } from "@utils/HttpUtilities";
import { DomainDesignation, ReauthorizationCalculation } from "@constants";

Vue.use(Vuex);

var baseURL = "/epp/public";

export const store = new Vuex.Store({
  state: {
    loading: false,
    cycleId: 0,
    entityId: 0,
    programId: 0,
    programCategoryId: 0,
    cycles: [],
    reportYears: [],
    entities: [],
    programs: [],
    programCategories: [],
    programTypes: [],
    postSecondaryLevels: [],
    entityProgramCategories: [],
    programCategorySummaries: [],
    stateSummaries: [],
    activeRequests: [],
    embeddedConfig: {},
    overallDomain: {
      DomainId: 0,
      Name: "Overall",
      Question: "",
      Why: "OverallDomain.Why moved to getter",
      Description: "",
      get PointsPossible() {
        let pointsPossible = 0;

        for (let i = 0; i < this.Indicators.length; i++) {
          pointsPossible += this.Indicators[i].PointsPossible;
        }

        return pointsPossible;
      },
      Indicators: [],
      SortOrder: 0,
    },
    domains: [],
    ineligibilityReasons: [],
    showVideoModal: false,
    scrollPosition: 0,
  },
  getters: {
    overallDomainWhy(state) {
      if (state.cycleId === 1) {
        return 'The IEPP overall performance designation is important in the reauthorization of preparation programs. The overall designation a program receives in the reports released in 2020 is for <strong>information only</strong>. Starting in 2022, the IEPP reports will have formal regulatory consequences for the renewal and continued approval of teacher preparation programs. These consequences are explained in the <a href="#">Educator Preparation Program Reauthorization Guide.</a>';
      }
      return "The IEPP overall performance designation is important in the reauthorization of preparation programs. The overall designation a program receives in the reports released in 2020 and 2021 is for <strong>information only</strong>. Starting in 2022, the IEPP reports will have formal regulatory consequences for the renewal and continued approval of teacher preparation programs. These consequences are explained in the Educator Preparation Program Reauthorization Guide, which is located under the \"Resources\" section of <a target='_blank' href='https://www.isbe.net/Pages/eppPublic.aspx'>this page</a>.";
    },
    cycle(state) {
      let target = state.cycles.find(x => x.CycleId === state.cycleId);
      return target ? target : {};
    },
    entity(state) {
      let aggregate = state.entities.find(cea => cea.cycleId === state.cycleId);
      if (!aggregate || !aggregate.entities) return {};
      let target = aggregate.entities.find(ent => ent.EntityId === state.entityId);
      //let target = state.entities.find(cea => cea.Cycle.CycleId === state.cycleId)//find(x => x.EntityId === state.entityId);
      return target ? JSON.parse(JSON.stringify(target)) : {};
    },
    entityProgramCategory(state) {
      let category = state.entityProgramCategories.find(
        x => x.ProgramCategoryId === state.programCategoryId && x.CycleId == state.cycleId && x.EntityId == state.entityId,
      );
      return category ? category : {};
    },
    program(state) {
      let target = state.programs.find(x => x.ProgramId === state.programId);
      return target ? target : {};
    },
    programCategorySummary(state) {
      let target = state.programCategorySummaries.find(
        x => x.ProgramCategoryId === state.programCategoryId && x.CycleId == state.cycleId && x.EntityId == state.entityId,
      );
      return target
        ? target
        : {
            ProgramTypes: {},
            PostSecondaryLevels: {},
            Facts: [],
          };
    },
    stateSummary(state) {
      let target = state.stateSummaries.find(x => x.CycleId == state.cycleId);
      return target
        ? target
        : {
            EntitiesCount: "N/A",
            ProgramsCount: "N/A",
            HistoriesCount: "N/A",
            Facts: [],
          };
    },
    searchData(state) {
      const results = [];
      let aggregate = state.entities.find(cea => cea.cycleId === state.cycleId);
      if (aggregate == null) return results;
      aggregate.entities.forEach(entity => {
        let programCategories = state.entityProgramCategories.filter(ep => ep.EntityId == entity.EntityId && ep.CycleId == state.cycleId);
        if (programCategories.length) {
          results.push({
            uid: entity.EntityId,
            entityName: entity.EntityName,
            display: entity.EntityName,
            type: "Institution",
            entityId: entity.EntityId,
          });

          programCategories.forEach(pc => {
            results.push({
              uid: `${entity.EntityId}_${pc.ProgramCategoryId}`,
              entityName: entity.EntityName,
              programName: pc.Description,
              display: `${entity.EntityName}, ${pc.Description}`,
              type: "Program",
              programCategoryId: pc.ProgramCategoryId,
              entityId: entity.EntityId,
            });
          });
        }
      });

      return results.sort((a, b) => {
        var textA = a.display.toUpperCase();
        var textB = b.display.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
    },
    headerVisible(state) {
      return document.querySelector("#app_header").offsetHeight > state.scrollPosition;
    },
    reportYears(state) {
      let cycle = state.cycles.find(x => x.CycleId === state.cycleId);
      cycle = cycle ? cycle : {};
      return state.reportYears.filter(f => cycle.StartReportYear <= f && f <= cycle.EndReportYear);
    },
  },
  mutations: {
    addRequest(state, data) {
      state.activeRequests.push(data);
    },
    removeRequest(state, data) {
      let index = state.activeRequests.findIndex(req => {
        if (req.type == data.type) {
          for (const property in data.options) {
            if (req[property] != data.options[property]) {
              return false;
            }
          }
          return true;
        }
        return false;
      });
      state.activeRequests.splice(index, 1);
    },
    addEntityPrograms(state, data) {
      let entities = state.entities
        .map(agg => ({
          Entities: agg.entities.map(entity => ({
            ...entity,
            CycleId: agg.cycleId,
          })),
        }))
        .flatMap(agg => agg.Entities)
        .sort((a, b) => a.CycleId - b.CycleId);

      var entityPrograms = [];
      data.forEach(ep => {
        let entity = entities.find(e => e.CycleId == ep.CycleId && e.EntityId == ep.EntityId);
        if (!entity) return;

        if (!entityPrograms.some(e => e.EntityId == ep.EntityId && e.ProgramCategoryId == ep.ProgramCategoryId)) {
          let programs = state.programs.filter(p => ep.ProgramIds.includes(p.ProgramId));
          entityPrograms.push({
            ...ep,
            EntityName: entity.EntityName,
            Programs: programs,
            Zip: entity.Zip,
            Description: state.programCategories.find(pc => pc.ProgramCategoryId == ep.ProgramCategoryId)?.Description,
            Designation: DomainDesignation.getDesignation(ep.Facts, state.overallDomain),
            ReauthorizationScore: ReauthorizationCalculation.getReauthorization(ep.Facts, state.overallDomain),
          });
        }
      });

      state.entityProgramCategories = state.entityProgramCategories.concat(entityPrograms);
    },
    addProgramCategorySummary(state, data) {
      state.programCategorySummaries.push(data);
    },
    addStateSummary(state, data) {
      state.stateSummaries.push(data);
    },
    addCycleEntities(state, data) {
      // TODO: Make entities make entities into a simple array, remove aggregation by cycle.
      state.entities.push(data);
    },
    set(state, data) {
      //state.entities = data.entities;
      state.embeddedConfig = data.embeddedConfig;
    },
    setCycles(state, data) {
      state.cycles = data;

      let year = Math.min(...data.map(m => m.StartReportYear));
      let endYear = Math.max(...data.map(m => m.EndReportYear));
      for (; year <= endYear; year++) state.reportYears.push(year);
    },
    setPrograms(state, data) {
      state.programs = data;
    },
    setProgramCategories(state, data) {
      state.programCategories = data;
    },
    setProgramTypes(state, data) {
      state.programTypes = data;
    },
    setPostSecondaryLevels(state, data) {
      state.postSecondaryLevels = data;
    },
    setDomains(state, data) {
      state.domains = data;

      state.domains.forEach(x => {
        state.overallDomain.Indicators = state.overallDomain.Indicators.concat(x.Indicators);
      });

      state.overallDomain.Question =
        `Is this program preparing strong, learner-ready teachers for classrooms? This is scored on ${state.overallDomain.Indicators.length}` +
        `${state.overallDomain.Indicators.length > 1 ? "indicators" : "indicator"} organized into ${state.domains.length} data domains.`;

      state.domains = state.domains.map(x => {
        x.Name = parseMergeCodes(state, x.Name);
        x.Description = parseMergeCodes(state, x.Description);
        x.Question = parseMergeCodes(state, x.Question);
        x.Why = parseMergeCodes(state, x.Why);
        x.Indicators = x.Indicators.map(y => {
          y.Name = parseMergeCodes(state, y.Name);
          y.Description = parseMergeCodes(state, y.Description);
          y.Question = parseMergeCodes(state, y.Question);
          return y;
        });
        return x;
      });
    },
    setIneligibilityReasons(state, data) {
      state.ineligibilityReasons = data;
    },
    setEntityPrograms(state, data) {
      let entities = state.entities
        .map(agg => ({
          Entities: agg.entities.map(entity => ({
            ...entity,
            CycleId: agg.cycleId,
          })),
        }))
        .flatMap(agg => agg.Entities)
        .sort((a, b) => a.CycleId - b.CycleId);

      var entityPrograms = [];
      data.forEach(ep => {
        let entity = entities.find(e => e.CycleId == ep.CycleId && e.EntityId == ep.EntityId);
        if (!entity) return;

        if (!entityPrograms.some(e => e.EntityId == ep.EntityId && e.ProgramCategoryId == ep.ProgramCategoryId)) {
          let programs = state.programs.filter(p => ep.ProgramIds.includes(p.ProgramId));
          entityPrograms.push({
            ...ep,
            EntityName: entity.EntityName,
            Programs: programs,
            Zip: entity.Zip,
            Description: state.programCategories.find(pc => pc.ProgramCategoryId == ep.ProgramCategoryId)?.Description,
            Designation: DomainDesignation.getDesignation(ep.Facts, state.overallDomain),
            ReauthorizationScore: ReauthorizationCalculation.getReauthorization(ep.Facts, state.overallDomain),
          });
        }
      });

      state.entityProgramCategories = entityPrograms
        .sort((a, b) => (a.EntityName > b.EntityName ? 1 : b.EntityName > a.EntityName ? -1 : 0))
        .sort((a, b) => (a.Description > b.Description ? 1 : b.Description > a.Description ? -1 : 0));
    },
    setEmbeddedConfig(state, data) {
      state.embeddedConfig = data;
    },
    setLoading(state, data) {
      state.loading = data;
    },
    setEntityId(state, data) {
      state.entityId = data;
    },
    setProgramId(state, data) {
      state.programId = data;
    },
    setProgramCategoryId(state, data) {
      state.programCategoryId = data;
    },
    setCycleId(state, data) {
      state.cycleId = data;
    },
    setShowVideoModal(state, isOpen) {
      if (state.showVideoModal != isOpen) state.showVideoModal = isOpen;
    },
    setScrollPosition(state, position) {
      state.scrollPosition = position;
    },
  },
  actions: {
    setLoading(context, isLoading) {
      if ((context.loading != isLoading && isLoading === true) || isLoading === false) context.commit("setLoading", isLoading);
    },
    setEntityId(context, entityId) {
      if (context.entityId != entityId) context.commit("setEntityId", isNaN(entityId) ? 0 : +entityId);
    },
    setProgramId(context, programId) {
      if (context.programId != programId) context.commit("setProgramId", isNaN(programId) ? 0 : +programId);
    },
    setProgramCategoryId(context, programCategoryId) {
      if (context.programCategoryId != programCategoryId) context.commit("setProgramCategoryId", isNaN(programCategoryId) ? 0 : +programCategoryId);
    },
    setCycleId(context, cycleId) {
      if (context.cycleId != cycleId) context.commit("setCycleId", isNaN(cycleId) ? 0 : +cycleId);
    },
    load(context) {
      context.commit("setLoading", true);

      return handleFetch("/EPP/V2/Public/Program/GetCycles", "get")
        .then(results => {
          context.commit("setCycles", results);

          let cycleId = Math.max(...results.map(m => m.CycleId));
          context.commit("setCycleId", cycleId);

          return Promise.all([
            handleFetch("/epp/reports/publicsnapshot", "get"),
            handleFetch("/EPP/V2/Public/Program/GetPrograms", "get"),
            handleFetch("/EPP/V2/Public/Program/GetCategories", "get"),
            handleFetch("/EPP/V2/Public/Program/GetTypes", "get"),
            handleFetch("/EPP/V2/Public/Program/GetPostSecondaryLevels", "get"),
            handleFetch("/EPP/V2/Public/Program/GetIPPDomains", "get"),
            handleFetch("/EPP/V2/Public/Program/GetIPPIneligibilityReasons", "get"),
            handleFetch(`/EPP/V2/Public/Entity/GetByCycle?cycleId=${cycleId}`, "get"),
            handleFetch(`/EPP/V2/Public/Program/GetEntityPrograms?cycleId=${cycleId}`, "get"),
            handleFetch(`/EPP/V2/Public/State/GetSummary?cycleId=${cycleId}`, "get"),
          ]);
        })
        .then(
          results => {
            context.commit("setEmbeddedConfig", results[0].embeddedConfig);
            context.commit("setPrograms", results[1]);
            context.commit("setProgramCategories", results[2]);
            context.commit("setProgramTypes", results[3]);
            context.commit("setPostSecondaryLevels", results[4]);
            context.commit("setDomains", results[5]);
            context.commit("setIneligibilityReasons", results[6]);
            context.commit("addCycleEntities", { cycleId: context.state.cycleId, entities: results[7] });
            context.commit("setEntityPrograms", results[8]);
            context.commit("addStateSummary", results[9]);
          },
          err => {
            console.error(err);
          },
        )
        .finally(() => {
          context.commit("setLoading", false);
        });
    },
    loadCycleData(context, cycleId) {
      if (!cycleId) return;
      //Do nothing if data already in store
      if (context.state.entities.some(e => e.cycleId == cycleId)) return;

      if (!context.state.activeRequests.find(req => req.type == "CycleData" && req.cycleId == cycleId)) {
        context.commit("addRequest", {
          type: "CycleData",
          options: {
            cycleId: cycleId,
          },
        });

        context.commit("setLoading", true);

        return Promise.all([
          handleFetch(`/EPP/V2/Public/Entity/GetByCycle?cycleId=${cycleId}`, "get"),
          handleFetch(`/EPP/V2/Public/Program/GetEntityPrograms?cycleId=${cycleId}`, "get"),
          handleFetch(`/EPP/V2/Public/State/GetSummary?cycleId=${cycleId}`, "get"),
        ])
          .then(results => {
            context.commit("addCycleEntities", { cycleId: cycleId, entities: results[0] });
            context.commit("addEntityPrograms", results[1]);
            context.commit("addStateSummary", results[2]);
          })
          .finally(() => {
            context.commit("removeRequest", {
              type: "CycleData",
              options: {
                cycleId: cycleId,
              },
            });
            context.commit("setLoading", false);
          });
      }
    },
    loadProgramCategorySummary(context, options) {
      if (!options || !options.programCategoryId || !options.entityId || !options.cycleId) return;

      //Do nothing if data already in store
      if (
        context.state.programCategorySummaries.find(
          cat => cat.ProgramCategoryId == options.programCategoryId && cat.EntityId == options.entityId && cat.CycleId == options.cycleId,
        )
      )
        return;

      //Only send request if there is not already an active one
      if (
        !context.state.activeRequests.find(
          req =>
            req.type == "ProgramSummaryCategory" &&
            req.ProgramCategoryId == options.programCategoryId &&
            req.entityId == options.entityId &&
            req.cycleId == options.cycleId,
        )
      ) {
        context.commit("addRequest", {
          type: "ProgramSummaryCategory",
          options: {
            programCategoryId: options.programCategoryId,
            entityId: options.entityId,
            cycleId: options.cycleId,
          },
        });
        context.commit("setLoading", true);
        let endpoint = `/EPP/V2/Public/Program/GetCategorySummary?programCategoryId=${options.programCategoryId}&entityId=${options.entityId}&cycleId=${options.cycleId}`;
        axios
          .get(endpoint)
          .then(response => {
            if (response.data) {
              if (
                !context.state.programCategorySummaries.find(
                  cat => cat.ProgramCategoryId == options.programCategoryId && cat.EntityId == options.entityId && cat.CycleId == options.cycleId,
                )
              )
                context.commit("addProgramCategorySummary", response.data.data);
            }
          })
          .finally(() => {
            context.commit("removeRequest", {
              type: "ProgramSummaryCategory",
              options: {
                programCategoryId: options.programCategoryId,
                entityId: options.entityId,
                cycleId: options.cycleId,
              },
            });
            context.commit("setLoading", false);
          });
      }
    },
    getZipsInRange(context, options) {
      return handleFetch(`${baseURL}/zip/${options.zip}/${options.miles}`, "get");
    },
    openVideoModal(context, isOpen) {
      context.commit("setShowVideoModal", isOpen);
    },
    trackScrollPosition(context) {
      let last_known_scroll_position = 0;
      let ticking = false;

      document.addEventListener("scroll", function () {
        last_known_scroll_position = window.scrollY;

        if (!ticking) {
          window.requestAnimationFrame(function () {
            context.commit("setScrollPosition", last_known_scroll_position);
            ticking = false;
          });

          ticking = true;
        }
      });
    },
  },
});

function parseMergeCodes(state, string) {
  // TODO: Possibly unnecessary escape characters
  // eslint-disable-next-line no-useless-escape
  let mergeCodeRegex = /\#\#(.*?)\#\#/g;

  if (mergeCodeRegex.test(string)) {
    let codes = string.match(mergeCodeRegex).map(x => {
      while (x.indexOf("#") > -1) {
        x = x.replace("#", "");
      }
      return x;
    });
    let results = {};

    for (let i = 0; i < codes.length; i++) {
      let code = codes[i];
      if (!results[`##${code}##`]) {
        switch (getCodeName(code)) {
          case "ReportYearSeries": {
            let params = getParams(code);
            results[`##${code}##`] = ReportYearSeries(params);
            break;
          }
        }
      }
    }

    for (let key in results) {
      string = string.split(key).join(results[key]);
    }
  }

  return string;

  function getParams(code) {
    var paramRegex = /\((.*?)\)/g;
    let matches = code.match(paramRegex);
    return matches[0] ? eval(matches[0]) : {};
  }

  function getCodeName(code) {
    return code.split("(")[0];
  }

  function ReportYearSeries(params) {
    let reportYears = [];
    for (let i = 0; i < params.include.length; i++) {
      let targetIndex = params.include[i];
      reportYears.push(state.reportYears[targetIndex]);
    }

    if (reportYears.length == 0) return "";
    else if (reportYears.length == 1) return reportYears[0];
    else {
      let last = reportYears[reportYears.length - 1];
      reportYears.pop();
      return `${reportYears.join(", ")}${reportYears.length > 1 ? "," : ""} ${params.conjunction ? params.conjunction : "and"} ${last}`;
    }
  }
}
